import { useState } from 'react';
import { Chip } from '@weareopenr/component-library';
import type { ColourVariant } from '@weareopenr/component-library';
import {
  isRunActive,
  usePermissions,
  useProvisioningMutations,
  useProvisioningRuns,
} from '../../hooks';
import type {
  IProvisioningRun,
  IProvisioningStep,
  ITenantSummary,
  ProvisioningRunStatus,
  ProvisioningStepStatus,
} from '../../types';
import styles from './TenantProvisioning.module.scss';

export const RUN_COLOUR: Record<ProvisioningRunStatus, ColourVariant> = {
  pending: 'honey',
  dispatched: 'honey',
  running: 'honey',
  succeeded: 'kale',
  failed: 'tomato',
};

const RUN_LABEL: Record<ProvisioningRunStatus, string> = {
  pending: 'Starting',
  dispatched: 'Waiting for GitHub',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
};

const STEP_GLYPH: Record<ProvisioningStepStatus, string> = {
  pending: '○',
  running: '◐',
  done: '●',
  failed: '✕',
  skipped: '–',
};

const KIND_LABEL = { create: 'Create', update: 'Re-apply' } as const;

const linkLabel = (step: IProvisioningStep): string => {
  if (step.github_run_id) return 'Open GitHub run';
  if (step.workflow) return 'Open workflow';
  if (step.name === 'workos_organisation' || step.name === 'first_admin') return 'Open in WorkOS';
  return 'Open';
};

const when = (value: string | null) => (value ? new Date(value).toLocaleString('en-GB') : '—');

const duration = (run: IProvisioningRun): string | null => {
  const start = run.dispatched_at ?? run.created_at;
  const end = run.finished_at ?? (isRunActive(run) ? new Date().toISOString() : run.updated_at);
  const seconds = Math.max(
    0,
    Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000),
  );
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

interface StepRowProps {
  run: IProvisioningRun;
  step: IProvisioningStep;
  canProvision: boolean;
  busy: boolean;
  onSet: (status: ProvisioningStepStatus) => void;
}

const StepRow = ({ run, step, canProvision, busy, onSet }: StepRowProps) => {
  const manual = step.kind === 'manual';
  const open = step.status === 'pending' || step.status === 'running';
  const actionable = manual && canProvision && run.status === 'succeeded';
  return (
    <li className={styles.step} data-status={step.status}>
      <span className={styles.glyph} aria-hidden="true">
        {STEP_GLYPH[step.status]}
      </span>
      <div className={styles.stepBody}>
        <div className={styles.stepHead}>
          <span className={styles.stepLabel}>{step.label}</span>
          <span className={styles.stepKind}>{manual ? 'manual' : 'automatic'}</span>
          <span className={styles.stepStatus}>{step.status}</span>
        </div>
        {step.detail ? <p className={styles.stepDetail}>{step.detail}</p> : null}
        {step.workflow &&
        step.status === 'running' &&
        step.github_run_id &&
        run.current_step &&
        isRunActive(run) ? (
          <p className={styles.stepNow}>Now: {run.current_step}</p>
        ) : null}
        {step.workflow && step.status === 'running' && !step.github_run_id ? (
          <p className={styles.stepNow}>Dispatched, waiting for GitHub to start the run</p>
        ) : null}
        <div className={styles.stepFoot}>
          {step.link ? (
            <a href={step.link} target="_blank" rel="noreferrer" className={styles.link}>
              {linkLabel(step)} ↗
            </a>
          ) : null}
          {step.done_at ? (
            <span className={styles.muted}>
              {step.status} {when(step.done_at)}
              {step.done_by ? ` by ${step.done_by}` : ''}
            </span>
          ) : null}
          {actionable && open ? (
            <>
              <button
                type="button"
                className={styles.small}
                disabled={busy}
                onClick={() => onSet('done')}
              >
                Mark done
              </button>
              <button
                type="button"
                className={styles.smallGhost}
                disabled={busy}
                onClick={() => onSet('skipped')}
              >
                Skip
              </button>
            </>
          ) : null}
          {actionable && !open ? (
            <button
              type="button"
              className={styles.smallGhost}
              disabled={busy}
              onClick={() => onSet('pending')}
            >
              Undo
            </button>
          ) : null}
          {manual && !actionable && open && run.status !== 'succeeded' ? (
            <span className={styles.muted}>after the automatic steps succeed</span>
          ) : null}
        </div>
      </div>
    </li>
  );
};

interface RunCardProps {
  run: IProvisioningRun;
  canProvision: boolean;
  busy: boolean;
  onStep: (step: string, status: ProvisioningStepStatus) => void;
}

const RunCard = ({ run, canProvision, busy, onStep }: RunCardProps) => {
  const done = run.steps.filter(
    (step) => step.status === 'done' || step.status === 'skipped',
  ).length;
  return (
    <article className={styles.run} aria-label={`${KIND_LABEL[run.kind]} run`}>
      <header className={styles.runHead}>
        <div className={styles.runTitle}>
          <Chip label={RUN_LABEL[run.status]} backgroundColour={RUN_COLOUR[run.status]} />
          <strong>{KIND_LABEL[run.kind]}</strong>
          <code className={styles.code}>{run.workflow.workflow}</code>
          <span className={styles.muted}>@ {run.workflow.ref}</span>
        </div>
        <div className={styles.runMeta}>
          <span>Started {when(run.created_at)}</span>
          <span>by {run.created_by}</span>
          {duration(run) ? <span>{duration(run)}</span> : null}
          <span>{`${done}/${run.steps.length} steps`}</span>
          {run.github_run_url ? (
            <a href={run.github_run_url} target="_blank" rel="noreferrer" className={styles.link}>
              GitHub run {run.github_run_id} ↗
            </a>
          ) : null}
        </div>
      </header>
      {run.status === 'dispatched' ? (
        <p className={styles.muted}>
          Dispatched {run.workflow.workflow}. GitHub usually starts the run within a few seconds;
          the page refreshes on its own.
        </p>
      ) : null}
      {run.error ? (
        <p className={styles.error} role="alert">
          {run.error}
        </p>
      ) : null}
      {run.inputs.admin_email ? (
        <p className={styles.muted}>
          First admin <code className={styles.code}>{run.inputs.admin_email}</code>
          {run.inputs.domains.length > 0
            ? `, domains ${run.inputs.domains.join(', ')}`
            : ', no email domains'}
        </p>
      ) : null}
      {run.inputs.provision_sftp ? (
        <p className={styles.muted}>
          With Zonal SFTP: user{' '}
          <code className={styles.code}>{run.inputs.zonal_tenant_ssh_username}</code>
          {run.inputs.zonal_tenant_ssh_public_key ? ', key supplied' : ', no key yet'}
        </p>
      ) : null}
      <ol className={styles.steps}>
        {run.steps.map((step) => (
          <StepRow
            key={step.name}
            run={run}
            step={step}
            canProvision={canProvision}
            busy={busy}
            onSet={(status) => onStep(step.name, status)}
          />
        ))}
      </ol>
    </article>
  );
};

export const TenantProvisioning = ({ tenant }: { tenant: ITenantSummary }) => {
  const { data: runs = [], isLoading, error } = useProvisioningRuns(tenant.name);
  const { start, setStep } = useProvisioningMutations(tenant.name);
  const { canProvision } = usePermissions();
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const busy = start.isLoading || setStep.isLoading;
  const active = runs.some(isRunActive);
  const latest = runs[0];
  const canRetry = canProvision && !active && latest?.status === 'failed' && !tenant.secret_present;
  const canReapply = canProvision && !active && tenant.secret_present;
  const startError = start.error as { message?: string } | null;

  const startRun = (action: 'create' | 'update') => {
    const inputs = latest?.inputs ?? {
      admin_email: null,
      domains: [],
      provision_sftp: false,
      zonal_tenant_ssh_username: null,
      zonal_tenant_ssh_public_key: null,
    };
    start.mutate({ action, ...inputs }, { onSuccess: () => setConfirmUpdate(false) });
  };

  if (isLoading) return <p className={styles.muted}>Loading provisioning runs…</p>;
  if (error)
    return (
      <p className={styles.error}>
        Could not load: {(error as { message?: string }).message ?? 'unknown error'}
      </p>
    );

  return (
    <div className={styles.wrap}>
      <div className={styles.topRow}>
        <p className={styles.muted}>
          {runs.length === 0
            ? 'No provisioning runs recorded. This tenant predates Nexus or was created by hand.'
            : `${runs.length} run${
                runs.length === 1 ? '' : 's'
              }. Infrastructure comes from deploy-tenant-${
                tenant.environment
              }.yml in the infrastructure repo; the other steps are a checklist.`}
        </p>
        <div className={styles.topActions}>
          {canRetry ? (
            <button
              type="button"
              className={styles.primary}
              disabled={busy}
              onClick={() => startRun('create')}
            >
              Retry create
            </button>
          ) : null}
          {canReapply && !confirmUpdate ? (
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              onClick={() => setConfirmUpdate(true)}
            >
              Re-apply infrastructure
            </button>
          ) : null}
          {canReapply && confirmUpdate ? (
            <span className={styles.confirm}>
              Runs the playbook again with action=update. Safe for an unchanged tenant.
              <button
                type="button"
                className={styles.primary}
                disabled={busy}
                onClick={() => startRun('update')}
              >
                Run now
              </button>
              <button
                type="button"
                className={styles.ghost}
                onClick={() => setConfirmUpdate(false)}
              >
                Cancel
              </button>
            </span>
          ) : null}
        </div>
      </div>
      {startError ? (
        <p className={styles.error} role="alert">
          {startError.message ?? 'Could not start the run'}
        </p>
      ) : null}
      {runs.map((run) => (
        <RunCard
          key={run.id}
          run={run}
          canProvision={canProvision}
          busy={busy}
          onStep={(step, status) => setStep.mutate({ runId: run.id, step, status })}
        />
      ))}
    </div>
  );
};
